import {useIntl} from 'react-intl';
import React, {useCallback} from 'react';

import SegmentControl from './SegmentControl';
import {useSegmentContext} from './context/context';
import {ISegmentDto} from './context/SegmentDtoInterface';

import './SegmentsTimeline.scss';

interface SegmentsTimelineProps {
  className?: string;

  onSegmentBlur?: (segmentId: string) => void;
  onSegmentHover?: (segmentId: string) => void;
  onSegmentRemove: (segmentId: string) => Promise<boolean>;
  convertSegmentPointToSeconds(
    segmentPoint: ISegmentDto['end'] | ISegmentDto['start']
  ): number;
  onSegmentUpdate: (
    segmentId: string,
    newStart: number,
    newEnd: number
  ) => Promise<boolean>;
}

const SegmentsTimeline = ({
  className = '',

  onSegmentBlur,
  onSegmentHover,
  onSegmentUpdate,
  onSegmentRemove,
  convertSegmentPointToSeconds,
}: SegmentsTimelineProps) => {
  const intl = useIntl();
  const {segmentList} = useSegmentContext();
  const dragContainerRef = React.createRef<HTMLDivElement>();

  const handleSegmentDrag = useCallback(
    async (segmentId: string, newStart: number, newEnd: number) => {
      return await onSegmentUpdate(segmentId, newStart, newEnd);
    },
    [onSegmentUpdate]
  );

  const formatTime = useCallback((time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);

    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  }, []);

  const getSegmentLegend = useCallback(
    (segment: ISegmentDto): string => {
      const endSeconds = convertSegmentPointToSeconds(segment.end);
      const startSeconds = convertSegmentPointToSeconds(segment.start);

      return intl.formatMessage(
        {id: 'segmentsTimeline.segmentControl.timeRange'},
        {
          end: formatTime(endSeconds),
          start: formatTime(startSeconds),
        }
      );
    },
    [intl, formatTime, convertSegmentPointToSeconds]
  );

  return (
    <div className={`segments-timeline ${className}`}>
      <div className="guideline" />

      <section className="segments-container" ref={dragContainerRef}>
        {segmentList.getAllSegments().map(segment => (
          <SegmentControl
            key={`segment-${segment.id}`}
            segment={segment.clone()}
            dragContainerRef={dragContainerRef}
            getSegmentLegend={getSegmentLegend}
            onRemove={() => onSegmentRemove(segment.id)}
            onBlur={() => onSegmentBlur && onSegmentBlur(segment.id)}
            onHover={() => onSegmentHover && onSegmentHover(segment.id)}
            onDrag={async (newStart: number, newEnd: number) =>
              await handleSegmentDrag(segment.id, newStart, newEnd)
            }
          />
        ))}
      </section>
    </div>
  );
};

export default SegmentsTimeline;
